import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Utils } from '@client/utility'

export type ArchiveChat = {
  chat_uuid: string
  char_uuid: string
  description: string
  profile_image: string
  is_pinned: boolean
  name: string
  messageCount: number
}

interface ChatTable {
  chat_uuid: string
  char_uuid: string
  description: string
  profile_image: string | null
  is_pinned: boolean
  name: string | null
  is_temporary: boolean
  created_at: string
}

interface UseArchiveChatPageAPIProps {
  chats: ArchiveChat[]
  isLoading: boolean
  handleCardClick: (chatUuid: string) => void
  handleDeleteChat: (chatUuid: string) => Promise<void>
  handlePinChat: (chatUuid: string) => Promise<void>
  hasNextPage: boolean
  hasPreviousPage: boolean
  goToNextPage: () => void
  goToPreviousPage: () => void
  setChatType: (type: 'normal' | 'archived') => void
  chatType: 'normal' | 'archived'
}

export function useArchiveChatPageAPI(
  open: boolean,
  onClose: () => void,
  chatsPerPage: number
): UseArchiveChatPageAPIProps {
  const [chats, setChats] = useState<ArchiveChat[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [hasPreviousPage, setHasPreviousPage] = useState(false)
  const [chatType, setChatTypeState] = useState<'normal' | 'archived'>('normal')
  const navigate = useNavigate()

  const setChatType = useCallback((type: 'normal' | 'archived') => {
    setChatTypeState(type)
    setCurrentPage(0)
  }, [])

  const fetchChats = useCallback(
    async (page: number, force = false) => {
      setIsLoading(true)
      try {
        const from = page * chatsPerPage
        const to = from + chatsPerPage

        const isTemporary = chatType === 'normal'

        const { data: chatData, count } = await Utils.db.select<ChatTable>({
          tables: 'chats',
          columns: 'chat_uuid,char_uuid,description,profile_image,is_pinned,name,is_temporary',
          countOption: 'exact',
          match: { is_temporary: isTemporary },
          range: { from, to },
          orderBy: { column: 'created_at', ascending: false },
          nocache: force,
        })

        const rows = chatData ?? []
        
        const totalCount = count || 0
        const nextPageExists = (page + 1) * chatsPerPage < totalCount
        const previousPageExists = page > 0

        setHasNextPage(nextPageExists)
        setHasPreviousPage(previousPageExists)

        const chatUuids = rows.map((c) => c.chat_uuid)

        let countsMap: Record<string, number> = {}
        
        if (chatUuids.length > 0) {
          const { data: countsData } = await Utils.db.select<{
            chat_uuid: string
            message_counts: number
          }>({
            tables: 'chat_message_counts',
            columns: 'chat_uuid,message_counts',
            match: { chat_uuid: chatUuids },
            nocache: force,
          })

          countsMap =
            countsData?.reduce((acc, cur) => {
              acc[cur.chat_uuid] = cur.message_counts
              return acc
            }, {} as Record<string, number>) || {}
        }

        const list: ArchiveChat[] = rows
          .map((chat) => ({
            chat_uuid: chat.chat_uuid,
            char_uuid: chat.char_uuid,
            description: chat.description,
            profile_image: chat.profile_image || '',
            is_pinned: chat.is_pinned,
            name: chat.name || 'Unknown',
            messageCount: countsMap[chat.chat_uuid] || 0,
          }))
          .sort((a, b) => Number(b.is_pinned) - Number(a.is_pinned))

        setChats(list)
      } catch (err) {
        setChats([])
        setHasNextPage(false)
        setHasPreviousPage(false)
      } finally {
        setIsLoading(false)
      }
    },
    [chatsPerPage, chatType]
  )

  useEffect(() => {
    if (open) fetchChats(currentPage)
  }, [open, currentPage, fetchChats])

  const handleCardClick = useCallback(
    (chatUuid: string) => {
      navigate(`/chat/${chatUuid}`)
      onClose()
    },
    [navigate, onClose]
  )

  const handleDeleteChat = useCallback(
    async (chatUuid: string) => {
      try {
        await Utils.db.remove({ tables: 'chats', match: { chat_uuid: chatUuid } })
        await fetchChats(currentPage, true)
      } catch {}
    },
    [fetchChats, currentPage]
  )

  const handlePinChat = useCallback(
    async (chatUuid: string) => {
      try {
        const currentChat = chats.find(c => c.chat_uuid === chatUuid)
        await Utils.db.update({
          tables: 'chats',
          values: { is_pinned: !currentChat?.is_pinned },
          match: { chat_uuid: chatUuid },
        })
        await fetchChats(currentPage, true)
      } catch {}
    },
    [fetchChats, currentPage, chats]
  )

  const goToNextPage = useCallback(() => {
    if (hasNextPage) setCurrentPage((p) => p + 1)
  }, [hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) setCurrentPage((p) => p - 1)
  }, [hasPreviousPage])

  return {
    chats,
    isLoading,
    handleCardClick,
    handleDeleteChat,
    handlePinChat,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    setChatType,
    chatType,
  }
}